import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Truck, Bell, Rocket, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

function AlertForm({ city, waitingCount }) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (phone.replace(/\D/g, "").length < 9) { toast.error("Entre un numéro valide (ex. 06 12 34 56 78)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/city-alerts", { city, phone });
      setDone(data);
      toast.success(data.already ? "Tu es déjà inscrit(e) pour cette ville 👌" : `Alerte activée pour ${city} 🔔`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur, réessaie");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="city-alert-success" className="flex items-start gap-3 bg-neon-green/10 border border-neon-green/50 p-3">
        <Check className="h-5 w-5 text-neon-green shrink-0 mt-0.5" />
        <p className="text-sm text-zinc-200">C'est noté. Tu seras prévenu(e) par SMS/WhatsApp dès qu'un livreur ouvre à <span className="text-neon-green font-bold">{city}</span>.
          {done.count > 1 && <span className="block text-zinc-400 text-xs mt-1">{done.count} personnes attendent déjà l'ouverture ici.</span>}
        </p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-2" data-testid="city-alert-form">
      <input
        data-testid="city-alert-phone"
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="06 12 34 56 78"
        className="w-full bg-ink border border-zinc-700 text-white px-3 py-2.5 text-sm focus:outline-none focus:border-neon-magenta"
      />
      <button data-testid="city-alert-submit" disabled={busy} className="w-full bg-neon-magenta text-white font-anton uppercase py-2.5 hover:bg-neon-purple transition-colors disabled:opacity-50">
        {busy ? "…" : "Préviens-moi"}
      </button>
      <p className="text-zinc-600 text-[11px]">{waitingCount > 0 ? `${waitingCount} personne${waitingCount > 1 ? "s" : ""} attend${waitingCount > 1 ? "ent" : ""} déjà à ${city}. ` : ""}Numéro utilisé uniquement pour cette alerte.</p>
    </form>
  );
}

export function CityNoVendorOptions({ city, waitingCount = 0 }) {
  const navigate = useNavigate();
  return (
    <section data-testid="city-no-vendor-options" className="mb-10">
      <p className="text-neon-green text-xs uppercase font-bold tracking-widest mb-1">Pas encore de livreur à {city} — 3 façons d'avancer</p>
      <h2 className="font-anton text-2xl sm:text-3xl uppercase text-white mb-5">On te livre <span className="neon-text-green">quand même</span></h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          data-testid="landing-national-cta"
          onClick={() => navigate("/boutique/national")}
          className="group relative text-left bg-ink-surface border-2 border-neon-green p-5 flex flex-col hover:shadow-[0_0_25px_rgba(157,255,0,0.35)] transition-all"
        >
          <span className="absolute -top-2.5 left-4 bg-neon-green text-black text-[10px] uppercase font-bold px-2 py-0.5">Dispo maintenant</span>
          <Truck className="h-8 w-8 text-neon-green mb-3" />
          <h3 className="font-anton text-xl uppercase text-white leading-tight">Colis discret <span className="text-neon-green">48h</span></h3>
          <p className="text-zinc-400 text-sm mt-2 flex-1">Le catalogue complet livré chez toi à {city} par la boutique nationale. Emballage neutre, scellé anti-odeur.</p>
          <span className="mt-4 inline-flex items-center justify-center gap-2 bg-neon-green text-black font-anton uppercase px-4 py-2.5 group-hover:bg-white transition-colors">Commander en 48h →</span>
        </button>

        <div data-testid="landing-alert-card" className="bg-ink-surface border border-neon-magenta/60 p-5 flex flex-col">
          <Bell className="h-8 w-8 text-neon-magenta mb-3" />
          <h3 className="font-anton text-xl uppercase text-white leading-tight">Alerte <span className="text-neon-magenta">ouverture</span></h3>
          <p className="text-zinc-400 text-sm mt-2 mb-3">Laisse ton numéro : on te prévient dès qu'un livreur STUFF s'active à {city}.</p>
          <AlertForm city={city} waitingCount={waitingCount} />
        </div>

        <Link
          to="/recrutement"
          data-testid="landing-recruit-cta"
          className="group text-left bg-ink-surface border border-zinc-700 p-5 flex flex-col hover:border-neon-magenta transition-colors"
        >
          <Rocket className="h-8 w-8 text-zinc-300 group-hover:text-neon-magenta transition-colors mb-3" />
          <h3 className="font-anton text-xl uppercase text-white leading-tight">Deviens <span className="text-zinc-300 group-hover:text-neon-magenta transition-colors">le vendeur</span></h3>
          <p className="text-zinc-400 text-sm mt-2 flex-1">Ville libre : ouvre ta boutique STUFF à {city}, stock fourni, 0€ d'entrée, et récupère tout le marché local.</p>
          <span className="mt-4 inline-flex items-center justify-center gap-2 border border-zinc-600 text-white font-anton uppercase px-4 py-2.5 group-hover:border-neon-magenta group-hover:text-neon-magenta transition-colors">Je candidate</span>
        </Link>
      </div>
    </section>
  );
}
